import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import { ArrowForward, NavigateBefore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Field, Form } from "react-final-form";
import BITextFieldForm from "../../../components/common/Forms/TextField/TextField";
import { composeValidators, required } from "../../../utils/validatorsEn";
import { colors } from "../../../theme/colors";

import HeaderImage from "../../../assets/detalles-nubes.png";

import { cc_expires_format, numeric_format } from "../../../utils/functions";

const minimumAmount =
  (min = 20) =>
  (value) => {
    const numValue = parseFloat(value);
    return numValue && numValue >= min
      ? undefined
      : `The minimum amount is Q${min}`;
  };

const isNumeric = (value) => {
  const numValue = parseFloat(value);
  return !isNaN(numValue) && numValue > 0
    ? undefined
    : "Please enter a valid amount";
};

const ContentWrapper = ({ children }) => (
  <Box
    sx={{
      maxWidth: "525px",
      margin: "2rem auto",
      padding: "0 0rem",
    }}
  >
    {children}
  </Box>
);

const StyledTitle = ({ children }) => (
  <Typography
    variant="h6"
    sx={{
      color: colors?.primary,
      fontWeight: "bold",
      fontSize: "28px",
      textAlign: "center",
    }}
  >
    {children}
  </Typography>
);

export default function PurchaseDetails({
  prevStep,
  nextStep,
  purchaseData,
  updatePurchaseData,
}) {
  const [buttonHover, setButtonHover] = useState(false);
  const navigate = useNavigate();

  const handleBack = () => {
    if (prevStep) {
      prevStep();
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = (formProps) => {
    if (updatePurchaseData) {
      updatePurchaseData(formProps);
    }

    if (nextStep) {
      nextStep();
    } else {
      navigate("/en/pay", { state: { amount: formProps.amount } });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: colors.white.main,
        paddingY: "20px",
      }}
    >
      <Container maxWidth="md">
        <div style={{ maxWidth: "85%", margin: "auto" }}>
          <Box display={{ xs: "none", sm: "block" }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              alignItems="center"
            >
              <Grid item>
                <IconButton
                  onClick={() =>
                    (window.location.href = "https://www.inspiraairlines.com/")
                  }
                >
                  <NavigateBefore
                    sx={{
                      color: colors.primary,
                      fontSize: "30px",
                    }}
                  />
                </IconButton>
              </Grid>
              <Grid item lg={6} sm={7} xl={5}>
                <StyledTitle>Purchase Details</StyledTitle>
              </Grid>
            </Grid>
          </Box>

          <Box display={{ xs: "block", sm: "none" }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={1}>
                <IconButton
                  onClick={() =>
                    (window.location.href = "https://www.inspiraairlines.com/")
                  }
                >
                  <NavigateBefore
                    sx={{
                      color: colors.primary,
                      fontSize: "30px",
                    }}
                  />
                </IconButton>
              </Grid>
              <Grid item xs={11}>
                <StyledTitle>Purchase Details</StyledTitle>
              </Grid>
            </Grid>
          </Box>
        </div>

        <Paper
          elevation={3}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            margin: "20px auto",
            maxWidth: "525px",
            boxShadow: "none",
            border: "1px solid #00216A",
          }}
        >
          <Box
            component="img"
            src={HeaderImage}
            alt="Background image"
            sx={{
              height: "200px",
              width: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />

          <Box sx={{ padding: "33px 27px" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: "bold",
                marginTop: "0px",
                color: colors.headerbk,
                fontSize: "24px",
              }}
            >
              Unique Flights!
            </Typography>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: "bold",
                marginBottom: "8px",
                color: colors.headerbk,
                fontSize: "18px",
              }}
            >
              Starting from Q20 onwards
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: "22px",
                color: colors.headerbk,
                fontSize: "1rem",
              }}
            >
              You can help people from every corner of Guatemala achieve
              their dreams through education.
            </Typography>
          </Box>
        </Paper>

        <ContentWrapper>
          <Form onSubmit={handleSubmit} initialValues={purchaseData || {}}>
            {(formProps) => (
              <form
                noValidate
                autoComplete="none"
                onSubmit={formProps.handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      name="amount"
                      component={BITextFieldForm}
                      variant="outlined"
                      label="Enter your donation amount"
                      required
                      placeholder="20.00"
                      validate={composeValidators(
                        required,
                        isNumeric,
                        minimumAmount(20)
                      )}
                      InputProps={{
                        startAdornment: (
                          <Typography
                            sx={{ color: "#8E9292", marginRight: "4px" }}
                          >
                            Q
                          </Typography>
                        ),
                      }}
                      format={(value) => numeric_format(String(value))}
                    />
                  </Grid>
                </Grid>

                <Button
                  onMouseOver={() => setButtonHover(true)}
                  onMouseOut={() => setButtonHover(false)}
                  color={buttonHover ? "primary" : "secondary"}
                  variant="contained"
                  sx={{
                    width: "100%",
                    borderRadius: "8px",
                    boxShadow: "none",
                    backgroundColor: colors.primary,
                    color: colors.white.main,
                    textTransform: "none",
                    fontWeight: 700,
                    height: "48px",
                    marginTop: "1.5rem",
                    maxWidth: "600px",
                    "&:disabled": {
                      backgroundColor: "#ccc",
                      color: "#666",
                    },
                  }}
                  type="submit"
                  disabled={!formProps.valid}
                >
                  Next
                </Button>
              </form>
            )}
          </Form>
        </ContentWrapper>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "#00216A",
            fontSize: "1rem",
            marginTop: "1.5rem",
          }}
        >
          2026 © All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
